import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listLeads, listVisitsBetween, registrationAlerts } from '../lib/api';
import { countdown, dateTime, formatPhone, isOverdue, relative, stageLabel, stageTone } from '../lib/format';
import type { Lead, RegistrationAlert, Visit } from '../lib/types';
import { Badge, Empty, PageHeader } from '../components/ui';
import { LeadDrawer } from './LeadDrawer';

const OPEN = ['new', 'qualified', 'visit_booked', 'visit_done', 'negotiation'];

function istDayBounds(now = new Date()) {
  const ist = new Date(now.getTime() + 5.5 * 3600e3);
  const start = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()) - 5.5 * 3600e3;
  return { start: new Date(start), end: new Date(start + 86400e3) };
}

export function RepToday() {
  const { me, isManager, projects } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [regAlerts, setRegAlerts] = useState<RegistrationAlert[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [params, setParams] = useSearchParams();
  const openId = params.get('lead');

  const load = useCallback(async () => {
    const { start, end } = istDayBounds();
    const [ls, vs, ra] = await Promise.all([listLeads().catch(() => []), listVisitsBetween(start.toISOString(), end.toISOString()).catch(() => []), registrationAlerts().catch(() => [])]);
    setLeads(ls); setVisits(vs); setRegAlerts(ra); setLoaded(true); setNow(Date.now());
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); const c = setInterval(() => setNow(Date.now()), 15000); return () => { clearInterval(t); clearInterval(c); }; }, [load]);

  const mine = useMemo(() => leads.filter((l) => l.owner_id === me?.id && OPEN.includes(l.stage) && !l.is_test), [leads, me]);
  const { end } = istDayBounds(new Date(now));
  const visitLeadIds = new Set(visits.filter((v) => v.status === 'booked').map((v) => v.lead_id));

  const respond = mine.filter((l) => l.response_due_at).sort((a, b) => a.response_due_at!.localeCompare(b.response_due_at!));
  const needs = mine.filter((l) => l.needs_human_at && !l.response_due_at);
  const overdue = mine.filter((l) => l.next_step_at && isOverdue(l.next_step_at, now) && !visitLeadIds.has(l.id) && !l.response_due_at);
  const today = mine.filter((l) => l.next_step_at && !isOverdue(l.next_step_at, now) && new Date(l.next_step_at) < end && !visitLeadIds.has(l.id));
  const noStep = mine.filter((l) => !l.next_step_at && !l.response_due_at && !l.needs_human_at);
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const myVisits = visits.filter((v) => v.status === 'booked' && (v.owner_id === me?.id || isManager)).filter((v) => !leadById.get(v.lead_id)?.is_test);

  const name = (l?: Lead) => l?.buyer?.name || formatPhone(l?.buyer?.phone_e164 ?? '') || 'Lead';
  const open = (id: string) => setParams((p) => { p.set('lead', id); return p; });
  const close = useCallback(() => setParams((p) => { p.delete('lead'); return p; }), [setParams]);
  const myRegs = regAlerts.filter((a) => !a.is_test && (!isManager || leadById.get(a.lead_id)?.owner_id === me?.id));
  const nothing = loaded && !myRegs.length && !respond.length && !needs.length && !overdue.length && !today.length && !myVisits.length && !noStep.length;

  return (
    <div>
      <PageHeader title={`Today${me ? `, ${me.full_name.split(' ')[0]}` : ''}`} subtitle={`${mine.length} open lead${mine.length === 1 ? '' : 's'} with you`} />
      {nothing && <Empty title="All clear">No calls due, no visits today, and every lead has a next step.</Empty>}

      <Section title="Respond now" tone="bad" items={respond} render={(l) => {
        const cd = countdown(l.response_due_at, now)!;
        return <Row key={l.id} onClick={() => open(l.id)} title={name(l)} sub={l.assign_reason ?? stageLabel[l.stage]} right={<Badge tone={cd.late ? 'bad' : 'warn'}>{cd.text}</Badge>} />;
      }} />
      <Section title="Needs you" tone="bad" items={needs} render={(l) => (
        <Row key={l.id} onClick={() => open(l.id)} title={name(l)} sub={l.needs_human_reason ?? ''} right={<Badge tone="bad">Needs you</Badge>} />
      )} />
      <Section title="Site visits today" tone="accent" items={myVisits} render={(v) => {
        const l = leadById.get(v.lead_id);
        return <Row key={v.id} onClick={() => open(v.lead_id)} title={name(l)} sub={`${projects.find((p) => p.id === v.project_id)?.name ?? 'Visit'} · confirm with the buyer`} right={<Badge tone="accent">{dateTime(v.scheduled_at)}</Badge>} />;
      }} />
      <Section title="Builder registrations" tone="warn" items={myRegs} render={(a) => (
        <Row key={a.registration_id} onClick={() => open(a.lead_id)} title={`${a.buyer_name ?? `L-${a.ref_no}`} · ${a.project_name}`}
          sub={a.alert === 'ended' ? 'Registration ended: register again or ask the builder to extend it' : a.alert === 'ending' ? 'Registration ends soon: push for the visit or booking' : 'Builder has not acknowledged it yet: follow up with them'}
          right={<Badge tone={a.alert === 'not_acknowledged' ? 'warn' : 'bad'}>{a.alert === 'ended' ? `Ended ${-a.days_left}d ago` : a.alert === 'ending' ? `${a.days_left}d left` : 'Waiting'}</Badge>} />
      )} />
      <Section title="Overdue follow-ups" tone="bad" items={overdue} render={(l) => (
        <Row key={l.id} onClick={() => open(l.id)} title={name(l)} sub={l.next_step_note ?? ''} right={<Badge tone="bad">{relative(l.next_step_at, now)}</Badge>} />
      )} />
      <Section title="Later today" tone="cold" items={today} render={(l) => (
        <Row key={l.id} onClick={() => open(l.id)} title={name(l)} sub={l.next_step_note ?? ''} right={<Badge tone="cold">{dateTime(l.next_step_at)}</Badge>} />
      )} />
      <Section title="No next step" tone="warn" items={noStep} render={(l) => (
        <Row key={l.id} onClick={() => open(l.id)} title={name(l)} sub="Set a dated next step so this lead is not forgotten" right={<Badge tone={stageTone[l.stage]}>{stageLabel[l.stage]}</Badge>} />
      )} />

      {openId && <LeadDrawer leadId={openId} onClose={close} onChanged={load} />}
    </div>
  );
}

function Section<T>({ title, items, render, tone }: { title: string; items: T[]; render: (x: T) => ReactNode; tone: 'bad' | 'warn' | 'accent' | 'cold' }) {
  if (!items.length) return null;
  const color = { bad: 'text-bad', warn: 'text-warn', accent: 'text-accent', cold: 'text-cold' }[tone];
  return (
    <section className="mb-5">
      <h2 className={`mb-2 text-sm font-semibold uppercase tracking-wider ${color}`}>{title} · {items.length}</h2>
      <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">{items.map(render)}</ul>
    </section>
  );
}

function Row({ title, sub, right, onClick }: { title: string; sub: string; right: ReactNode; onClick: () => void }) {
  return (
    <li>
      <button onClick={onClick} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface2">
        <span className="min-w-0"><span className="block truncate font-medium">{title}</span><span className="block truncate text-sm text-muted">{sub}</span></span>
        <span className="shrink-0">{right}</span>
      </button>
    </li>
  );
}
